// Coarse, privacy-safe description of how a peer connection was actually
// established, for analytics. We only ever surface enumerated candidate types
// (host / srflx / prflx / relay), the transport protocol and a rounded RTT —
// never IPs, ports, peer ids or anything that could identify a network. This is
// what turns "it sometimes doesn't connect" into a distribution, and shows what
// share of sessions run over a TURN relay (a direct Cloudflare cost).

// Find the candidate pair the connection settled on. Prefer the transport's
// explicit pointer; fall back to the nominated/selected succeeded pair, which is
// how browsers without `selectedCandidatePairId` expose it.
function selectedPair(stats) {
  let transportPairId = null;
  stats.forEach((r) => {
    if (r.type === 'transport' && r.selectedCandidatePairId) {
      transportPairId = r.selectedCandidatePairId;
    }
  });
  if (transportPairId) {
    const pair = stats.get(transportPairId);
    if (pair) return pair;
  }
  let fallback = null;
  stats.forEach((r) => {
    if (r.type !== 'candidate-pair') return;
    if (r.state !== 'succeeded') return;
    if (r.nominated || r.selected) fallback = fallback || r;
  });
  return fallback;
}

// Returns { local, remote, protocol, rtt } or null if stats aren't available
// yet. `local`/`remote` are candidateType enums; `protocol` is the local
// candidate's transport (udp/tcp); `rtt` is the current pair RTT in ms, rounded.
export async function describeConnection(pc) {
  if (!pc || typeof pc.getStats !== 'function') return null;
  let stats;
  try {
    stats = await pc.getStats();
  } catch (e) {
    return null;
  }
  const pair = selectedPair(stats);
  if (!pair) return null;
  const local = stats.get(pair.localCandidateId);
  const remote = stats.get(pair.remoteCandidateId);
  const rtt = typeof pair.currentRoundTripTime === 'number'
    ? Math.round(pair.currentRoundTripTime * 1000)
    : null;
  return {
    local: (local && local.candidateType) || 'unknown',
    remote: (remote && remote.candidateType) || 'unknown',
    protocol: (local && local.protocol) || 'unknown',
    rtt,
  };
}

// True when either end of the selected pair is a TURN relay, i.e. the session is
// not a direct P2P path. Callers use this to size relay usage.
export function isRelayed(desc) {
  return !!desc && (desc.local === 'relay' || desc.remote === 'relay');
}
